import { DateField } from '@ovComponents/2-component/dateField/dateField';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { isValidEmail } from '@utils/common';
import dayjs from 'dayjs';
import { Alert, Box, Grid, Typography } from 'hephaestus/web';
import { Error as ErrorIcon } from 'hephaestus/webIcons';
import { kebabCase } from 'lodash-es';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';

export const UserVerificationVisual = ({
  options,
  userData,
  loading,
  updateUser,
  continueFunc,
  grid = false,
  updateMode = false,
  workflowCompletion,
  isMaxAttemptsExceeded = false,
  failedVerificationAttemps = 0,
  allowedAttemps = 0,
}: {
  options: any;
  userData: any;
  loading: boolean;
  updateUser: any;
  continueFunc: any;
  grid?: boolean;
  updateMode?: boolean;
  workflowCompletion?: any;
  isMaxAttemptsExceeded?: boolean;
  failedVerificationAttemps?: number;
  allowedAttemps?: number;
}) => {
  const { translateBackendString } = useLocalization();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation('client');
  const [focused, setFocused] = useState<string[]>([]);
  const [invalidFieldsList, setInvalidFieldsList] = useState<string[]>([]);
  const [invalidEmail, setInvalidEmail] = useState(false);

  const submit = () => {
    const fields = [];
    if (userData.primaryEmail && !isValidEmail(userData.primaryEmail)) {
      fields.push('primaryEmail');
      setInvalidEmail(true);
    } else {
      setInvalidEmail(false);
    }

    setFocused(fields);
    setInvalidFieldsList(fields);

    if (fields.length === 0 && !isMaxAttemptsExceeded) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsPersonalInformationContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      continueFunc();
    }
  };

  const update = (newValue: any) => {
    updateUser(newValue);
  };

  const error = (key: string): boolean =>
    ((!userData?.[key] && focused.includes(key)) ||
      invalidFieldsList.includes(key)) &&
    options?.[key]?.required !== FieldRequiredType.NOT_REQUIRED;

  const formDisabled = loading || isMaxAttemptsExceeded;

  const lockMessage = () =>
    options.customLockedMessage
      ? translateBackendString(options.customLockedMessage)
      : t('pageConfiguration:notEditableMessage');

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='user-verification-form'
      showSubmitButton={!updateMode || options.displayUpdateButton}
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled: formDisabled,
        dataTestId: `user-verification-${kebabCase(translateBackendString(options?.customTitle || options?.title))}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackendString(options?.title)}
          <Box
            sx={{
              display: 'inline-flex',
            }}
          >
            {additionalInfo(
              translateBackendString,
              options?.title?.additionalInfo,
            ) && (
              <InfoDialog
                information={
                  additionalInfo(
                    translateBackendString,
                    options?.title?.additionalInfo,
                  ) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}

      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          component={'div'}
          sx={{
            mb: 3,
            table: { width: '100%' },
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              m: 0,
              '& > *': { m: 0 },
            }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}

      <Grid container spacing={2}>
        {options?.firstName?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='legal-first-name'
              onChange={(e: any) =>
                update({ ...userData, firstName: e.target.value })
              }
              disabled={
                loading ||
                options?.firstName?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackendString(options?.firstName?.label)}
              fullWidth
              value={userData.firstName ?? ''}
              onBlur={() => setFocused([...focused, 'firstName'])}
              error={error('firstName')}
              locked={
                options?.firstName?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.firstName?.additionalInfo,
              )}
            />
          </Grid>
        )}
        {options?.lastName?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='legal-last-name'
              onChange={(e: any) =>
                update({ ...userData, lastName: e.target.value })
              }
              disabled={
                loading ||
                options?.lastName?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackendString(options?.lastName?.label)}
              fullWidth
              value={userData.lastName ?? ''}
              onBlur={() => setFocused([...focused, 'lastName'])}
              error={error('lastName')}
              locked={
                options?.lastName?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.lastName?.additionalInfo,
              )}
            />
          </Grid>
        )}
        {options?.dateOfBirth?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 6 : 12 }}>
            <DateField
              dataTestId='date-of-birth'
              onChange={(date: any) =>
                update({
                  ...userData,
                  dateOfBirth: dayjs(date?.toString()).format('YYYY-MM-DD'),
                })
              }
              disabled={
                loading ||
                options?.dateOfBirth?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackendString(options?.dateOfBirth?.label)}
              lockMessage={lockMessage()}
              fullWidth
              value={userData.dateOfBirth}
              onBlur={() => setFocused([...focused, 'dateOfBirth'])}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.dateOfBirth?.additionalInfo,
              )}
              error={error('dateOfBirth')}
            />
          </Grid>
        )}
        {options?.primaryEmail?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 6 : 12 }}>
            <TextField
              testId='primary-email'
              onChange={(e: any) =>
                update({ ...userData, primaryEmail: e.target.value })
              }
              disabled={
                loading ||
                options?.primaryEmail?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackendString(options?.primaryEmail?.label)}
              fullWidth
              value={userData.primaryEmail ?? ''}
              onBlur={() => setFocused([...focused, 'primaryEmail'])}
              error={error('primaryEmail')}
              errorText={
                invalidEmail ? t('components:fieldErrors.email') : undefined
              }
              locked={
                options?.primaryEmail?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.primaryEmail?.additionalInfo,
              )}
            />
          </Grid>
        )}
        {isMaxAttemptsExceeded && (
          <Grid size={{ xs: 12, md: 12 }}>
            <Typography variant='bodyLarge' sx={{ mt: 1, mb: 3 }}>
              {translateBackendString(options?.maxAttemptsExceededMessage)}
            </Typography>
            <Alert variant='outlined' icon={<ErrorIcon />} severity='error'>
              <Typography variant='bodySmall' component='p'>
                {t('shared:maxAttemptsExceeded')}
              </Typography>
            </Alert>
          </Grid>
        )}
        {failedVerificationAttemps > 0 &&
          allowedAttemps > failedVerificationAttemps && (
            <Grid size={{ xs: 12, md: 12 }}>
              <Alert variant='outlined' icon={<ErrorIcon />} severity='error'>
                <Typography variant='bodySmall' component='p'>
                  {t('shared:failedVerificationAttemp', {
                    attempt: allowedAttemps - failedVerificationAttemps,
                  })}
                </Typography>
              </Alert>
            </Grid>
          )}
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default UserVerificationVisual;
